import ImageWithFallback from "@/components/application/image-with-fallback";
import StorefrontLayout from "@/components/application/storefront-layout";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item";
import {
  storefront_product_path,
  storefront_store_root_path,
} from "@/lib/routes";
import { getProductState } from "@/lib/utils";
import { StorefrontCollectionPageProps } from "@/types";
import { Link, usePage } from "@inertiajs/react";
import { ReactNode } from "react";
import useTranslations from "@/hooks/use-translations";

function CollectionPage() {
  const { current_store, collection } =
    usePage<StorefrontCollectionPageProps>().props;
  const { t } = useTranslations("storefront.collections.show");

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={storefront_store_root_path()}>{t("home")}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{t("collections")}</BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{collection.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <ItemGroup className="grid grid-cols-1 md:grid-cols-[repeat(4,minmax(280px,1fr))] gap-6">
        {collection.products.map((product) => (
          <Item key={product.id} variant="outline" asChild>
            <Link href={storefront_product_path(product.id)}>
              <ItemHeader className="relative aspect-square">
                <Badge
                  variant="secondary"
                  className="absolute left-0 top-1 z-1 text-sm md:text-xs"
                >
                  {product.system_code}
                </Badge>
                <Badge
                  variant="outline"
                  className="absolute right-0 top-1 z-1 text-sm md:text-xs"
                >
                  {getProductState(product, t)}
                </Badge>
                <ImageWithFallback
                  srcset={[product.custom_image_url, product.image_url]}
                  alt={product.name}
                  className="relative w-full rounded-sm m-auto object-contain aspect-square"
                />
              </ItemHeader>
              <ItemContent>
                <ItemTitle className="text-base md:text-sm">
                  {product.name}
                </ItemTitle>
                <ItemDescription>
                  <span className="font-semibold text-lg md:text-base">
                    {new Intl.NumberFormat(navigator.language, {
                      style: "currency",
                      currency: current_store.currency.toUpperCase() || "USD",
                    }).format(product.price_cents / 100)}
                  </span>
                </ItemDescription>
              </ItemContent>
            </Link>
          </Item>
        ))}
      </ItemGroup>
    </>
  );
}

CollectionPage.layout = (page: ReactNode) => (
  <StorefrontLayout children={page} />
);

export default CollectionPage;
